<template>
  <div class="aggregate-menu">
    <section class="menu-hero">
      <div>
        <p class="menu-eyebrow">Aggregate Workspace</p>
        <h1>集計情報管理</h1>
        <p class="menu-description">
          売上集計は「傾向を素早く掴むダッシュボード」、業者作業履歴は「年度の明細確認」に役割を分けています。
          用途に応じて入口を選べるように整理しました。
        </p>
      </div>

      <router-link :to="{ name: 'Home' }" class="btn btn-outline-primary">
        ホーム
      </router-link>
    </section>

    <section class="menu-grid">
      <router-link
        v-for="card in menuCards"
        :key="card.name"
        class="menu-card"
        :to="{ name: card.routeName }"
      >
        <span class="menu-card-tag">{{ card.tag }}</span>
        <h2>{{ card.name }}</h2>
        <p>{{ card.description }}</p>
        <strong>{{ card.helper }}</strong>
      </router-link>
    </section>
  </div>
</template>

<script>
import { computed, ref, watch, watchEffect } from 'vue';
import { useStore } from 'vuex';

const menuCards = [
  {
    name: '年間予定作業 売上集計',
    routeName: 'SalesByData',
    tag: 'Sales',
    description: '年間予定作業の売上を、カテゴリ比較と月次推移で確認します。',
    helper: '建物・顧客・業者・業務区分・担当者・月別',
  },
  {
    name: '臨時作業 売上集計',
    routeName: 'TemporaryWorkSalesByData',
    tag: 'Temporary',
    description: '臨時作業の売上を、偏りが見えやすいランキング形式で確認します。',
    helper: 'スポット案件の集中や繁忙月の把握に向いています',
  },
  {
    name: '全作業 売上集計',
    routeName: 'TotalSalesByData',
    tag: 'Total',
    description: '年間予定作業と臨時作業を統合し、全体の売上構造を俯瞰します。',
    helper: '経営確認や月次報告用の全体把握',
  },
  {
    name: '業者作業履歴集計',
    routeName: 'VendorWorkHistory',
    tag: 'History',
    description: '業者別の年間作業履歴と月別金額を明細表で確認します。',
    helper: '個別確認や請求根拠の追跡向け',
  },
];

export default {
  name: 'AggregateInfoMngMenu',
  setup() {
    const title = ref('請求管理');

    watchEffect(() => {
      document.title = title.value;
    });

    const store = useStore();
    const isAdmin = ref(false);
    const isLoggedIn = computed(() => store.state.isLoggedIn);
    const userRole = computed(() => store.state.userRole);

    watch(userRole, (newRole) => {
      isAdmin.value = newRole === '999' || newRole === '0';
    }, { immediate: true });

    return {
      isAdmin,
      isLoggedIn,
      menuCards,
    };
  },
};
</script>

<style scoped>
.aggregate-menu {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
}

.menu-hero,
.menu-card {
  border: 1px solid #d7e2ec;
  border-radius: 24px;
  background: #ffffff;
  box-shadow: 0 18px 40px rgba(18, 42, 68, 0.08);
}

.menu-hero {
  display: flex;
  justify-content: space-between;
  gap: 24px;
  align-items: flex-start;
  padding: 28px;
  background:
    radial-gradient(circle at top right, rgba(54, 122, 199, 0.14), transparent 28%),
    linear-gradient(135deg, rgba(54, 122, 199, 0.12), transparent 52%),
    #ffffff;
}

.menu-eyebrow {
  margin: 0;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #2b5f96;
}

.menu-hero h1 {
  margin: 8px 0 12px;
  font-size: 32px;
  font-weight: 700;
}

.menu-description {
  margin: 0;
  max-width: 760px;
  color: #60758b;
  line-height: 1.8;
}

.menu-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 18px;
}

.menu-card {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 22px;
  color: inherit;
  text-decoration: none;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.menu-card:hover {
  transform: translateY(-2px);
  border-color: #9ec1e4;
  box-shadow: 0 22px 44px rgba(18, 42, 68, 0.12);
}

.menu-card-tag {
  display: inline-flex;
  width: fit-content;
  padding: 6px 10px;
  border-radius: 999px;
  background: #eef6ff;
  color: #2b5f96;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.menu-card h2 {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
}

.menu-card p {
  margin: 0;
  color: #60758b;
  line-height: 1.8;
}

.menu-card strong {
  color: #1f3750;
  font-size: 14px;
}

@media (max-width: 900px) {
  .menu-grid {
    grid-template-columns: 1fr;
  }

  .menu-hero {
    flex-direction: column;
  }
}

@media (max-width: 768px) {
  .aggregate-menu {
    padding: 16px;
  }

  .menu-hero,
  .menu-card {
    padding: 18px;
    border-radius: 18px;
  }

  .menu-hero h1 {
    font-size: 28px;
  }
}
</style>
